/**
 * URL Parameter Service
 *
 * Handles parsing and updating URL query parameters for filter state synchronization.
 * All inputs are validated using FilterValidation utility for security and robustness.
 *
 * URL Parameter Format:
 * - dest: Destination tree ID(s) - single value or hyphen-separated (e.g., "ES" or "ES-IT-FR")
 * - date: Start date (YYYY-MM-DD or +N for relative)
 * - todate: End date (YYYY-MM-DD or +N for relative)
 * - days: Trip duration in days (number)
 * - dmode: `exact` when the user explicitly asked for their exact dates; absent means flexible
 * - odate / otodate: travel dates of the single offer a result-list card was clicked on. Read on
 *   the detail page to rank that offer to the top; never written by the app, only carried inward.
 * - type: Product types, hyphen-separated (string)
 * - pax: Room-based passenger data with 'r' prefix per room (e.g., "r35-35r10-7" = 2 adults in room 1, 1 adult + 1 child in room 2)
 * - att: Filter attributes, hyphen-separated `{groupId}_{optionId}` tokens from the curated filter config (e.g., "location_beach-type_halfboard")
 * - sort: Sort order (price | reco)
 * - p: Page number (number)
 *
 * @example
 * ```ts
 * // Parse current URL with single destination
 * const params = UrlParameterService.parseUrlParameters();
 * console.log(params.destinationId); // "ES" (string)
 *
 * // Parse URL with multiple destinations
 * // URL: ?dest=ES-IT-FR
 * const params2 = UrlParameterService.parseUrlParameters();
 * console.log(params2.destinationId); // ["ES", "IT", "FR"] (array)
 *
 * // Parse URL with room-based passengers
 * // URL: ?pax=r35-35r10-7
 * const params3 = UrlParameterService.parseUrlParameters();
 * console.log(params3.roomOccupancy);
 * // {
 * //   rooms: [
 * //     { persons: [{ type: 'adult' }, { type: 'adult' }] },
 * //     { persons: [{ type: 'child', age: 10 }, { type: 'child', age: 7 }] }
 * //   ]
 * // }
 *
 * // Update URL with single destination
 * UrlParameterService.updateUrlParameters({
 *   destinationId: "ES",
 *   startDate: new Date("2025-10-30"),
 *   duration: 7
 * });
 *
 * // Update URL with multiple destinations
 * UrlParameterService.updateUrlParameters({
 *   destinationId: ["ES", "IT", "FR"],
 *   startDate: new Date("2025-10-30"),
 *   duration: 7
 * });
 * ```
 */

import { FilterValidation } from '@/utils/validation/filterValidation';
import { parsePaxString, serializePaxString } from '@/utils/parsing/paxParser';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import { parseDurationMode } from '@/utils/filters/durationMode';
import type { UrlParameters } from '@/types/SearchDefaults';

export type { UrlParameters };

export class UrlParameterService {
  /**
   * Parse URL parameters with full validation
   *
   * Extracts filter state from current URL query parameters.
   * All values are validated - invalid inputs are logged and skipped.
   *
   * @returns Parsed and validated URL parameters
   *
   * @example
   * ```ts
   * // URL: ?dest=ES&date=2025-10-30&days=7&pax=r35-35r10
   * const params = UrlParameterService.parseUrlParameters();
   * // {
   * //   destinationId: "ES",  // single destination
   * //   startDate: Date("2025-10-30"),
   * //   duration: 7,
   * //   roomOccupancy: {
   * //     rooms: [
   * //       { persons: [{ type: 'adult' }, { type: 'adult' }] },
   * //       { persons: [{ type: 'child', age: 10 }] }
   * //     ]
   * //   }
   * // }
   *
   * // URL: ?dest=ES-IT-FR&date=2025-10-30&days=7
   * const params2 = UrlParameterService.parseUrlParameters();
   * // {
   * //   destinationId: ["ES", "IT", "FR"],  // multiple destinations
   * //   startDate: Date("2025-10-30"),
   * //   duration: 7
   * // }
   * ```
   */
  static parseUrlParameters(): UrlParameters {
    const params = new URLSearchParams(window.location.search);
    const result: UrlParameters = {};
    const errors: string[] = [];

    if (params.has('dest')) {
      const destValue = params.get('dest');
      const destinations = destValue
        ?.split('-')
        .map((d) => FilterValidation.validateDestinationId(d.trim()))
        .filter((d): d is string => d !== null);

      if (destinations && destinations.length > 0) {
        result.destinationId = destinations.length === 1 ? destinations[0] : destinations;
      } else {
        errors.push('Invalid destination ID(s)');
      }
    }

    if (params.has('date')) {
      const startDate = FilterValidation.validateDate(params.get('date')!);
      if (startDate) {
        result.startDate = startDate;
      } else {
        errors.push('Invalid start date');
      }
    }

    if (params.has('todate')) {
      const endDate = FilterValidation.validateDate(params.get('todate')!);
      if (endDate) {
        result.endDate = endDate;
      } else {
        errors.push('Invalid end date');
      }
    }

    if (result.startDate && result.endDate) {
      const rangeValid = FilterValidation.validateDateRange(result.startDate, result.endDate);
      const startBookable = FilterValidation.isStartDateBookable(result.startDate);
      if (!rangeValid || !startBookable) {
        console.warn(
          '[UrlParameterService] Date range invalid (must be future-dated and start before end)'
        );
        delete result.startDate;
        delete result.endDate;
        errors.push('Invalid date range');
      }
    }

    if (params.has('days')) {
      const duration = FilterValidation.validateDuration(params.get('days'));
      if (duration !== null) {
        result.duration = duration;
      } else {
        errors.push('Invalid duration');
      }
    }

    if (params.has('dmode')) {
      const mode = parseDurationMode(params.get('dmode'));
      if (mode !== undefined) {
        result.durationMode = mode;
      } else {
        errors.push('Invalid duration mode');
      }
    }

    if (params.has('odate')) {
      const offerStartDate = FilterValidation.validateDate(params.get('odate')!);
      if (offerStartDate) {
        result.offerStartDate = offerStartDate;
      } else {
        errors.push('Invalid offer start date');
      }
    }

    if (params.has('otodate')) {
      const offerEndDate = FilterValidation.validateDate(params.get('otodate')!);
      if (offerEndDate) {
        result.offerEndDate = offerEndDate;
      } else {
        errors.push('Invalid offer end date');
      }
    }

    if (
      result.startDate &&
      result.endDate &&
      result.duration !== undefined &&
      !FilterValidation.validatePeriodConsistency(result.startDate, result.endDate, result.duration)
    ) {
      console.warn('[UrlParameterService] Duration exceeds date range, dropping duration');
      delete result.duration;
      errors.push('Duration inconsistent with date range');
    }

    if (params.has('type')) {
      const types = params
        .get('type')!
        .split('-')
        .map((t) => t.trim())
        .filter((t) => t.length > 0 && t.length < 50);

      if (types.length > 0) {
        result.productTypes = types;
      }
    }

    if (params.has('pax')) {
      const paxString = params.get('pax')!;
      const roomOccupancy = parsePaxString(paxString);

      if (roomOccupancy) {
        result.roomOccupancy = roomOccupancy;
      } else {
        errors.push('Invalid pax format - must use room syntax (e.g., r35-35r10-7)');
      }
    }

    if (params.has('att')) {
      try {
        result.attributes = this.parseAttributesSafe(params.get('att')!);
      } catch (error) {
        console.warn('[UrlParameterService] Error parsing attributes:', error);
        errors.push('Invalid attributes');
      }
    }

    if (params.has('sort')) {
      const sortBy = FilterValidation.validateSortBy(params.get('sort'));
      if (sortBy) {
        result.sortBy = sortBy;
      } else {
        errors.push('Invalid sort option');
      }
    }

    if (params.has('fixedonly')) {
      result.fixedBookableOnly = params.get('fixedonly') === '1';
    }

    if (params.has('p')) {
      const page = FilterValidation.validatePage(params.get('p'));
      if (page !== null) {
        result.page = page;
      } else {
        errors.push('Invalid page number');
      }
    }

    if (errors.length > 0) {
      console.warn(`[UrlParameterService] URL parameter validation errors: ${errors.join(', ')}`);
    }

    return result;
  }

  /**
   * Update URL with current filter state
   *
   * Updates browser URL using history.replaceState() - no page reload.
   * Only includes parameters that have values (undefined values are skipped).
   *
   * @param params - Filter state to write to URL
   *
   * @example
   * ```ts
   * UrlParameterService.updateUrlParameters({
   *   destinationId: "ES",
   *   startDate: new Date("2025-10-30"),
   *   endDate: new Date("2025-11-06"),
   *   duration: 7,
   *   sortBy: "price"
   * });
   * // URL becomes: ?dest=ES&date=2025-10-30&todate=2025-11-06&days=7&sort=price
   * ```
   */
  static updateUrlParameters(params: UrlParameters): void {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    // Preserve debug parameter before clearing
    const debugValue = searchParams.get('debug');

    // Clear existing filter parameters (NOT debug)
    [
      'dest',
      'date',
      'todate',
      'days',
      'dmode',
      'odate',
      'otodate',
      'type',
      'pax',
      'att',
      'sort',
      'fixedonly',
      'p',
    ].forEach((key) => searchParams.delete(key));

    this.applyFilterParams(searchParams, params);

    if (debugValue) {
      searchParams.set('debug', debugValue);
    }

    // Update URL without page reload
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Build a URL string for a target page with the given filter parameters.
   *
   * Used by the search widget to redirect to a product-list page with all
   * search criteria encoded in the query string. The target product-list page
   * picks them up via `parseUrlParameters()` on its own load.
   *
   * Relative paths are resolved against the current origin. Absolute URLs are
   * accepted as-is. Existing query parameters on `targetUrl` are preserved
   * (e.g. tracking parameters); only the filter params are written.
   *
   * @param targetUrl - Target page URL (relative path or absolute URL)
   * @param params    - Filter parameters to encode
   * @returns Absolute URL string ready for `window.location.href`
   *
   * @example
   * ```ts
   * UrlParameterService.buildUrl('/cesty', {
   *   destinationId: ['ES', 'IT'],
   *   startDate: new Date('2025-10-30'),
   *   endDate: new Date('2025-11-06'),
   *   duration: 7,
   * });
   * // "https://example.com/cesty?dest=ES-IT&date=2025-10-30&todate=2025-11-06&days=7"
   * ```
   */
  static buildUrl(targetUrl: string, params: UrlParameters): string {
    const url = new URL(targetUrl, window.location.origin);
    this.applyFilterParams(url.searchParams, params);
    return url.toString();
  }

  /**
   * Write filter parameters into a `URLSearchParams` instance. Pure mutation —
   * no clearing, no preservation logic. Shared between `updateUrlParameters`
   * (current page sync) and `buildUrl` (target-page redirect).
   */
  private static applyFilterParams(searchParams: URLSearchParams, params: UrlParameters): void {
    if (params.destinationId) {
      const destValue = Array.isArray(params.destinationId)
        ? params.destinationId.join('-')
        : params.destinationId;
      searchParams.set('dest', destValue);
    }

    if (params.startDate) {
      searchParams.set('date', formatDateLocal(params.startDate));
    }

    if (params.endDate) {
      searchParams.set('todate', formatDateLocal(params.endDate));
    }

    if (params.duration !== undefined && params.duration > 0) {
      searchParams.set('days', params.duration.toString());
    }

    if (params.durationMode === 'exact') {
      searchParams.set('dmode', 'exact');
    }

    if (params.productTypes?.length) {
      searchParams.set('type', params.productTypes.join('-'));
    }

    if (params.roomOccupancy?.rooms?.length) {
      const paxString = serializePaxString(params.roomOccupancy);
      if (paxString) {
        searchParams.set('pax', paxString);
      }
    }

    if (params.attributes && params.attributes.length > 0) {
      searchParams.set('att', this.formatAttributes(params.attributes));
    }

    if (params.sortBy) {
      searchParams.set('sort', params.sortBy === 'price' ? 'price' : 'reco');
    }

    if (params.fixedBookableOnly) {
      searchParams.set('fixedonly', '1');
    }

    if (params.page && params.page > 1) {
      searchParams.set('p', params.page.toString());
    }
  }

  /**
   * Parse attributes safely with validation and XSS protection
   *
   * Format: "{groupId}_{optionId}-..." — hyphen-separated tokens from the
   * curated filter config. Each token has exactly one underscore separating
   * groupId from optionId. Invalid items are skipped, not throwing errors.
   * Mapping to composite keys happens in the filterConfiguration store.
   *
   * @param attStr - Attribute string from URL
   * @returns Validated and sanitized attributes array
   *
   * @example
   * ```ts
   * const attrs = parseAttributesSafe("location_beach-topics_hiking-type_halfboard");
   * // ["location_beach", "topics_hiking", "type_halfboard"]
   *
   * // XSS attempt blocked
   * const safe = parseAttributesSafe("key_<script>alert('xss')</script>");
   * // ["key_scriptalertxssscript"] (sanitized)
   * ```
   */
  private static parseAttributesSafe(attStr: string): string[] {
    const attributes: string[] = [];

    attStr.split('-').forEach((item) => {
      const parts = item.split('_');
      if (parts.length === 2) {
        const [category, value] = parts;
        const sanitizedCategory = FilterValidation.sanitizeAttribute(category);
        const sanitizedValue = FilterValidation.sanitizeAttribute(value);

        if (sanitizedCategory && sanitizedValue) {
          attributes.push(`${sanitizedCategory}_${sanitizedValue}`);
        }
      }
    });

    return attributes;
  }

  /**
   * Format attributes array to URL string
   *
   * Converts flat array format to hyphen-separated string.
   * Each attribute has format "{groupId}_{optionId}".
   *
   * @param attributes - Array of attributes in format ["location_beach", "type_halfboard"]
   * @returns Formatted string "location_beach-type_halfboard"
   *
   * @example
   * ```ts
   * const str = formatAttributes(["location_beach", "topics_hiking", "type_halfboard"]);
   * // "location_beach-topics_hiking-type_halfboard"
   * ```
   */
  private static formatAttributes(attributes: string[]): string {
    return attributes.join('-');
  }
}
